/**
 * Capital-gains tool API (admin-only, `capital_gains.run`; backend `routers/capital_gains.py`).
 *
 * Stateless: the chosen export files stay in the browser and are re-sent on every call
 * (preview, then download) — the backend stores nothing about the transactions.
 * Amounts arrive as decimal STRINGS at full precision; rates as ratios (0.48 = 48%).
 */

export interface CgIssue { portfolio: string; row: number | null; reason: string; question_key: string | null }
export interface CgAnswer { value: string; label: string; available: boolean; why_not: string }
/** A stop the user can resolve in-app: answer it, then Preview again. */
export interface CgQuestion {
  key: string; kind: string; portfolio: string; instrument: string; prompt: string; rows: number[];
  chosen: string | null; answers: CgAnswer[];
}
export interface CgCheck { name: string; ok: boolean; detail: string }
export interface CgReviewItem {
  type: string; portfolio: string; instrument: string; date: string | null; operation: string; detail: string;
}
export interface CgPortfolio {
  label: string; file_name: string; sha256: string; size_bytes: number; rows: number; operations: number;
  excluded_reversals: number; positions: number; open_lots: number; sales: number; lot_matches: number;
  closed_instruments: number; code_changes: number; cash_value_eur: string | null;
}
/** A tax of null = not computable: the group has uncertified sales (partial history). Never shown as 0. */
export interface CgYear {
  year: number; short_gain_eur: string; short_tax: string | null; long_gain_eur: string; long_tax: string | null;
  total_tax: string | null; short_uncertified: number; long_uncertified: number;
}
export interface CgIfSoldToday {
  year: number; realised_tax: string | null; combined_tax: string | null; extra_tax: string | null;
}
/** Partial history: one portfolio's certified window. */
export interface CgWindow {
  portfolio: string; day1: string | null; certified_from: string | null; uncertified_sales: number;
}
/** Partial history: what a security held before day 1, worked out back from today. */
export interface CgOpeningUnits {
  portfolio: string; instrument: string; name: string; isin: string | null; currency: string | null;
  quantity: string; sold_out_on: string | null; still_held: string; corrected: boolean;
  acquired_on: string | null; cost_eur: string | null;
}
export interface CgSnapshotLot {
  acquired_on: string | null; source: string; operation: string; quantity: string; cost_eur: string | null;
  certain: boolean;
}
export interface CgSnapshotPosition {
  instrument: string; name: string; isin: string | null; currency: string; quantity: string; certain: boolean;
  lots: CgSnapshotLot[];
}
/** The positions and lots at the end of a date: the certified date, or the date the user chose. */
export interface CgSnapshot { portfolio: string; date: string; kind: 'certified' | 'chosen'; positions: CgSnapshotPosition[] }
export interface CgTransferInLot {
  portfolio: string; instrument: string; name: string; isin: string | null; op_code: string;
  transfer_date: string; quantity: string;
  cost_eur: string | null;   // null = the export gives this transfer no EUR value (its cost must be supplied)
}
export interface CgPreview {
  run_id: string; client_label: string; as_of: string; price_date: string; price_date_defaulted: boolean;
  short_rate: string; long_rate: string; publishable: boolean; input_errors: string[]; blocking: CgIssue[];
  questions: CgQuestion[]; checks: CgCheck[]; review_items: CgReviewItem[]; portfolios: CgPortfolio[]; years: CgYear[];
  if_sold_today: CgIfSoldToday | null; transfer_in_lots: CgTransferInLot[];
  history: 'full' | 'partial'; lots_date: string | null; windows: CgWindow[]; opening_units: CgOpeningUnits[];
  snapshots: CgSnapshot[];
}

/** Partial history: the true acquisition of the units a security held before day 1 (both required). */
export interface CgOpening { portfolio: string; instrument: string; acquired_on: string; cost_eur: string }

/** A user-supplied correction for a transferred-in lot (its true acquisition date / EUR cost). */
export interface CgOverride {
  portfolio: string; instrument: string; op_code: string; acquired_on: string; cost_eur: string | null;
}

export interface CgInputs {
  files: { file: File; label: string }[];
  clientLabel: string;
  asOf: string;              // YYYY-MM-DD
  priceDate: string;         // YYYY-MM-DD or '' (= the calculation date)
  shortRatePct: number;      // 48 = 48%
  longRatePct: number;
  overrides: CgOverride[];
  decisions: { key: string; answer: string }[];   // answers to the questions of the last preview
  history: 'full' | 'partial';                     // partial: each export starts on its oldest operation
  opening: CgOpening[];                            // partial history only
  lotsDate: string;                                // YYYY-MM-DD or '' (no extra date)
}

function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

/** Percent (48) → ratio string ("0.48") without float noise. */
const pctToRatio = (pct: number): string => (Math.round(pct * 10000) / 1000000).toString();

function form(inputs: CgInputs): FormData {
  const f = new FormData();
  for (const { file, label } of inputs.files) {
    f.append('files', file);
    f.append('labels', label);
  }
  f.append('client_label', inputs.clientLabel);
  f.append('as_of', inputs.asOf);
  f.append('price_date', inputs.priceDate);
  f.append('short_rate', pctToRatio(inputs.shortRatePct));
  f.append('long_rate', pctToRatio(inputs.longRatePct));
  if (inputs.overrides.length) f.append('overrides', JSON.stringify(inputs.overrides));
  if (inputs.decisions.length) f.append('decisions', JSON.stringify(inputs.decisions));
  f.append('history', inputs.history);
  if (inputs.history === 'partial' && inputs.opening.length) f.append('opening', JSON.stringify(inputs.opening));
  if (inputs.lotsDate) f.append('lots_date', inputs.lotsDate);
  return f;
}

export const capitalGainsApi = {
  async preview(inputs: CgInputs): Promise<CgPreview> {
    const res = await fetch('/capital-gains/preview', { method: 'POST', credentials: 'include', body: form(inputs) });
    if (!res.ok) throw new Error(await detailOf(res, 'The exports could not be read.'));
    return res.json();
  },

  async workbook(inputs: CgInputs): Promise<{ blob: Blob; fileName: string; runId: string | null }> {
    const res = await fetch('/capital-gains/workbook', { method: 'POST', credentials: 'include', body: form(inputs) });
    if (!res.ok) throw new Error(await detailOf(res, 'The workbook could not be produced.'));
    return {
      blob: await res.blob(),
      fileName: fileNameFromCD(res.headers.get('Content-Disposition') ?? '', 'capital-gains.xlsx'),
      runId: res.headers.get('X-Capital-Gains-Run-Id'),
    };
  },
};
